<script setup>
import { ref } from 'vue'
import { useTestStore } from '../stores/testStore'

// Définissez les props acceptées par FileUploader
defineProps({
  title: {
    type: String,
    default: '', // Définissez une valeur par défaut
  },
});

const emit = defineEmits(['import-test', 'close']);

const store = useTestStore()
const errorMessage = ref('')
const successMessage = ref('')

// Fonction de validation du format WASPL
const validateWASPLFormat = (data) => {
  // Vérifier que c'est un objet
  if (!data || typeof data !== 'object') {
    throw new Error('Le fichier doit contenir un objet JSON valide');
  }

  // Champs requis pour un test WASPL
  const requiredFields = ['ID', 'title'];
  const missingFields = requiredFields.filter(field => !data[field]);

  if (missingFields.length > 0) {
    throw new Error(`Format WASPL invalide. Champs requis manquants: ${missingFields.join(', ')}`);
  }

  // Vérifier que elements existe et est un tableau (peut être vide pour les nouveaux tests)
  if (data.elements !== undefined && !Array.isArray(data.elements)) {
    throw new Error('Le champ "elements" doit être un tableau');
  }

  // Validation basique des métadonnées
  if (data.metadata && typeof data.metadata !== 'object') {
    throw new Error('Le champ "metadata" doit être un objet');
  }

  return true;
}

// Fonction de validation des images Base64
const validateImages = (elements) => {
  if (!elements || !Array.isArray(elements)) return true;

  const maxSizeMB = 0.4; // Taille max par image (configurable)
  const errors = [];

  elements.forEach((el, index) => {
    // Vérifier l'image principale de l'élément
    if (el.el_Image && typeof el.el_Image === 'string' && el.el_Image.startsWith('data:image')) {
      const sizeInMB = (el.el_Image.length * 3/4) / (1024 * 1024);
      if (sizeInMB > maxSizeMB) {
        errors.push(`Élément ${index + 1} (${el.el_Text || 'sans titre'}): image trop grande ${sizeInMB.toFixed(2)}MB (max ${maxSizeMB}MB)`);
      }
    }

    // Vérifier les images dans les interactions spécifiques
    if (el.el_InteractionData) {
      // HotSpot images
      if (el.el_InteractionData.backgroundImage && el.el_InteractionData.backgroundImage.startsWith('data:image')) {
        const sizeInMB = (el.el_InteractionData.backgroundImage.length * 3/4) / (1024 * 1024);
        if (sizeInMB > maxSizeMB) {
          errors.push(`Élément ${index + 1} HotSpot: image de fond trop grande ${sizeInMB.toFixed(2)}MB (max ${maxSizeMB}MB)`);
        }
      }

      // Choice/ChoiceBlock images dans les options
      if (el.el_InteractionData.options && Array.isArray(el.el_InteractionData.options)) {
        el.el_InteractionData.options.forEach((opt, optIndex) => {
          if (opt.image && opt.image.startsWith('data:image')) {
            const sizeInMB = (opt.image.length * 3/4) / (1024 * 1024);
            if (sizeInMB > maxSizeMB) {
              errors.push(`Élément ${index + 1}, Option ${optIndex + 1}: image trop grande ${sizeInMB.toFixed(2)}MB (max ${maxSizeMB}MB)`);
            }
          }
        });
      }
    }
  });

  if (errors.length > 0) {
    throw new Error(`Images trop volumineuses détectées:\n${errors.join('\n')}`);
  }

  return true;
}

const showMessage = (message, isError = false) => {
  if (isError) {
    errorMessage.value = message;
    successMessage.value = '';
  } else {
    successMessage.value = message;
    errorMessage.value = '';
  }

  // Auto-clear après 5 secondes
  setTimeout(() => {
    errorMessage.value = '';
    successMessage.value = '';
  }, 5000);
}

const handleFileUpload = (event) => {
  const file = event.target.files[0]
  if (!file) return;

  // Vérifier l'extension du fichier
  if (!file.name.endsWith('.json')) {
    showMessage('Veuillez sélectionner un fichier JSON valide (.json)', true);
    event.target.value = ''; // Reset input
    return;
  }

  const reader = new FileReader()
  reader.onload = (e) => {
    try {
      // 1. Parser le JSON
      const data = JSON.parse(e.target.result)

      // 2. Valider le format WASPL
      validateWASPLFormat(data);

      // 3. Valider les images
      if (data.elements && data.elements.length > 0) {
        validateImages(data.elements);
      }

      // 4. Tout est OK, sauvegarder
      store.setTestData(data)
      localStorage.setItem('testData', JSON.stringify(data))
      emit('import-test', data);

      // 5. Notification de succès
      const elementCount = data.elements ? data.elements.length : 0;
      showMessage(`✅ Test "${data.title}" importé avec succès! (${elementCount} élément${elementCount > 1 ? 's' : ''})`);

    } catch (error) {
      console.error('❌ Error during import:', error)

      // Gestion des différents types d'erreurs
      let errorMsg = 'Erreur lors de l\'import du fichier';

      if (error instanceof SyntaxError) {
        errorMsg = '❌ Fichier JSON invalide. Vérifiez la syntaxe du fichier.';
      } else {
        errorMsg = `❌ ${error.message}`;
      }

      showMessage(errorMsg, true);
    }

    // Reset input pour permettre de ré-importer le même fichier
    event.target.value = '';
  }

  reader.onerror = () => {
    showMessage('❌ Erreur lors de la lecture du fichier', true);
    event.target.value = '';
  }

  reader.readAsText(file)
}
</script>

<template>
  <div class="file-uploader-wrapper">
    <input hidden class="form-control" id="file" type="file" accept=".json" @change="handleFileUpload" placeholder="Import WASPL JSON file">

    <label for="file" class="btn-import">
      <i class="bi bi-upload"></i>
      <span>Import Test</span>
    </label>

    <!-- Messages de feedback -->
    <transition name="slide-fade">
      <div v-if="successMessage" class="alert alert-success alert-dismissible fade show mt-2" role="alert">
        <i class="bi bi-check-circle-fill me-2"></i>
        {{ successMessage }}
        <button type="button" class="btn-close" @click="successMessage = ''" aria-label="Close"></button>
      </div>
    </transition>

    <transition name="slide-fade">
      <div v-if="errorMessage" class="alert alert-danger alert-dismissible fade show mt-2" role="alert">
        <i class="bi bi-exclamation-triangle-fill me-2"></i>
        <div style="white-space: pre-line;">{{ errorMessage }}</div>
        <button type="button" class="btn-close" @click="errorMessage = ''" aria-label="Close"></button>
      </div>
    </transition>
  </div>
</template>

<style scoped>
.file-uploader-wrapper {
  display: inline-block;
  position: relative;
}

input[type="file"] {
  height: 0;
  width: 0;
  overflow: hidden;
}

/* Bouton Import moderne - style outline */
.btn-import {
  display: flex;
  align-items: center;
  gap: 8px;
  background: white;
  color: #1976D2;
  border: 2px solid #1976D2;
  border-radius: 8px;
  padding: 10px 20px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s ease;
  margin: 0;
}

.btn-import i {
  font-size: 1.1rem;
}

.btn-import:hover {
  background: #1976D2;
  color: white;
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.3);
  transform: translateY(-2px);
}

.btn-import:active {
  transform: translateY(0);
}

/* Alert styles */
.alert {
  position: fixed;
  top: 80px;
  right: 20px;
  max-width: 500px;
  z-index: 9999;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border-radius: 8px;
  padding: 12px 16px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.alert-success {
  background-color: #d4edda;
  border-color: #c3e6cb;
  color: #155724;
}

.alert-danger {
  background-color: #f8d7da;
  border-color: #f5c6cb;
  color: #721c24;
}

.alert i {
  font-size: 1.2rem;
  flex-shrink: 0;
}

.alert .btn-close {
  margin-left: auto;
  padding: 0;
  background: transparent;
  border: none;
  font-size: 1.2rem;
  cursor: pointer;
  opacity: 0.5;
  transition: opacity 0.2s;
}

.alert .btn-close:hover {
  opacity: 1;
}

/* Transitions */
.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.3s cubic-bezier(1, 0.5, 0.8, 1);
}

.slide-fade-enter-from {
  transform: translateX(20px);
  opacity: 0;
}

.slide-fade-leave-to {
  transform: translateX(20px);
  opacity: 0;
}
</style>